import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { push } from "connected-react-router";
import { makeStyles } from "@material-ui/core/styles";
import ee from "../../../services/earth-engine"; // @TODO remove this!
import { Box, Grid, Grow } from "@material-ui/core";
import ImageChooserCard from "../../components/map/ImageChooserCard";
import LoadedImagesAccordion from "../../components/map/LoadedImagesAccordion";
import { Actions as Map } from "../../../store/ducks/map";

// REPLACE WITH FLOOD MODULE ACTIONS
import { Actions as Acquisition } from "../../../store/ducks/acquisition";

import ActionsCard from "../../components/map/ActionsCard";
import { ShapeList } from "../../components";

import { useTranslation } from "react-i18next";
import TourGuider from "../../components/tour/TourGuider";
import { useLocalStorage } from "../../../common/utils";
import { Button } from "@material-ui/core";
import FloodControls from "../../components/map/FloodControls";
import FloodLegend from "../../components/map/FloodControls/FloodLegend";

// Add imports
import { TextField, Paper, Typography } from "@material-ui/core";
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from "@material-ui/pickers";

// useStyles is a hook for Material-UI's styling.
const useStyles = makeStyles((theme) => ({
  wrapper: {
    maxHeight: "100vh",
    flexGrow: 1,
    position: "relative",
  },
  mapContainer: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "flex-end",
    flexFlow: "column",
  },
  mapOverlay: {
    position: "absolute",
    top: 0,
    bottom: 0,
    zIndex: 10,
    pointerEvents: "none",
    "& > *": {
      pointerEvents: "none",
      "& > *": {
        pointerEvents: "auto",
      },
    },
  },
  controlPanel: {
    position: "absolute",
    bottom: theme.spacing(2),
    left: theme.spacing(2),
    width: "350px",
    zIndex: 1000,
  },
  card: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    height: "420px", // ✅ Increased height slightly to fit button
    boxShadow: theme.shadows[2],
    overflow: "hidden",
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between", // ✅ Ensures button stays at bottom
  },
  cardHeader: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    padding: theme.spacing(1.5, 2),
  },
  cardContent: {
    padding: theme.spacing(2),
    flexGrow: 1, // ✅ Allows content to take available space
  },
  section: {
    marginTop: theme.spacing(2),
    "&:first-child": {
      marginTop: 0,
    },
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "center", // ✅ Centers the button horizontally
    padding: theme.spacing(2, 0), // ✅ Adds spacing from content
  },
  button: {
    width: "90%", // ✅ Keeps button well-sized inside the card
  },
}));

// main function
const ProcessingPage = () => {
  const mapDiv = useRef();
  const mapRef = useRef();
  const drawingManager = useRef();

  const [mapsLoaded, setMapsLoaded] = useState(false);

  const [coordinates, setCoordinates] = useState(null);
  const [areaOfPolygon, setAreaOfPolygon] = useState(null);

  // check if the user is drawing a shape
  const isDrawing = useSelector((state) => state.map.currentlyDrawing);

  // get the map coordinates
  // const coordinates = useSelector((state) => state.acquisition.coordinates);
  const dispatch = useDispatch();
  // custom styles
  const classes = useStyles();
  // get the current language
  const [t] = useTranslation();

  const [isLegendVisible, setIsLegendVisible] = useState(false);

  const [geojsonUrl, setGeojsonUrl] = useState(null);
  const [rasterUrl, setRasterUrl] = useState(null);

  const [overlay, setOverlay] = React.useState(null);

  // Add new states
  const [selectedGeom, setSelectedGeom] = useState(null);
  // const [startDate, setStartDate] = useState(new Date("2000-01-01"));
  // const [endDate, setEndDate] = useState(new Date());
  const [threshold, setThreshold] = useState(30);
  const [step, setStep] = useState(1);

  const [analysisData, setAnalysisData] = useState({
    startDate: new Date("2010-01-01"),
    endDate: new Date(),
    threshold: 30,
  });

  // Check if Google Maps is loaded
  useEffect(() => {
    const checkMapsLoaded = setInterval(() => {
      if (window.google && window.google.maps) {
        setMapsLoaded(true);
        clearInterval(checkMapsLoaded);
      }
    }, 100);

    return () => clearInterval(checkMapsLoaded);
  }, []);

  useEffect(() => {
    if (!mapsLoaded) return;

    try {
      mapRef.current = new window.google.maps.Map(mapDiv.current, {
        center: { lat: 5.4844216, lng: -58.780906 },
        zoom: 6,
        scaleControl: true,
        mapTypeControl: true,
      });

      drawingManager.current = new window.google.maps.drawing.DrawingManager({
        drawingControl: false,
        drawingControlOptions: {
          position: window.google.maps.ControlPosition.TOP_CENTER,
          drawingModes: [window.google.maps.drawing.OverlayType.POLYGON],
        },
        polygonOptions: {
          fillColor: "#00B3A1",
          strokeColor: "#003832",
        },
      });

      window.google.maps.event.addListener(
        drawingManager.current,
        "overlaycomplete",
        handleDrawing
      );
    } catch (error) {
      console.error("Error initializing map:", error);
    }
  }, [mapsLoaded]);

  // function that displays a Geometry feature in ee with the given coordinates
  const displayROI = () => {
    if (coordinates) {
      // create a new ROI
      const feature = ee.Feature(ee.Geometry.Polygon([coordinates]));
      // get the border
      const border = feature.buffer(15).difference(feature);
      // display the border in the map in #00A391
      dispatch(Map.addEEFeature(border, "forms.map.roi", "#00A391", 0.6));
      // center the map on the coordinates
      dispatch(Map.centralizeMap(coordinates));
    }
  };

  let returnToBegin = function () {
    dispatch(push("/main/selection"));
  };

  const handleStartDrawing = (mode) => {
    drawingManager.current.setMap(mapRef.current);
    drawingManager.current.setDrawingMode(
      window.google.maps.drawing.OverlayType[mode.toUpperCase()]
    );
  };

  // Get coordinates from shape
  const getCoordinates = (shape) => {
    let coords = [];

    if (shape.type === "rectangle") {
      const bounds = shape.overlay.getBounds();
      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();
      coords = [
        [ne.lng(), ne.lat()],
        [ne.lng(), sw.lat()],
        [sw.lng(), sw.lat()],
        [sw.lng(), ne.lat()],
        [ne.lng(), ne.lat()],
      ];
    }

    if (shape.type === "polygon") {
      const path = shape.overlay.getPath();
      const array = path.getArray();
      coords = array.map((p) => [p.lng(), p.lat()]);
    }

    return coords;
  };

  const handleDrawing = (event) => {
    try {
      let coords = [];

      if (event.type === window.google.maps.drawing.OverlayType.RECTANGLE) {
        const bounds = event.overlay.getBounds();
        const ne = bounds.getNorthEast();
        const sw = bounds.getSouthWest();
        coords = [
          [ne.lng(), ne.lat()],
          [ne.lng(), sw.lat()],
          [sw.lng(), sw.lat()],
          [sw.lng(), ne.lat()],
          [ne.lng(), ne.lat()],
        ];
      } else if (
        event.type === window.google.maps.drawing.OverlayType.POLYGON
      ) {
        const path = event.overlay.getPath();
        coords = path.getArray().map((p) => [p.lng(), p.lat()]);
        // Close the polygon
        coords.push(coords[0]);
      }

      setOverlay(event.overlay);
      setCoordinates(coords);
      drawingManager.current.setDrawingMode(null);
      drawingManager.current.setMap(null);

      setSelectedGeom(coords);
      dispatch(Acquisition.setAOI(event.overlay, coords));

      console.log(coords);
    } catch (error) {
      console.error("Error handling drawing:", error);
    }
  };

  // Handle undoing the AOI
  const handleUndo = () => {
    if (overlay) {
      // Clear map overlay
      overlay.setMap(null);

      // Reset states
      setOverlay(null);
      setCoordinates(null);
      setSelectedGeom(null);

      // Reset drawing manager
      drawingManager.current.setMap(mapRef.current);
      drawingManager.current.setDrawingMode(null);

      // Update drawing state
      // dispatch(Map.setDrawing(false));
    }
  };

  const handleDataUpdate = (data) => {
    setAnalysisData(data);
    handleAnalysis(data);
  };

  const getMangroves = async () => {
    try {
      // Load the ESA WorldCover dataset
      const dataset = ee.ImageCollection("ESA/WorldCover/v200").first();
      const mangroveMask = dataset.select("Map").eq(95);
      const mangroves = dataset.updateMask(mangroveMask);

      // Visualization parameters
      const visualization = {
        min: 95,
        max: 95,
        palette: ["000000", "blue"],
      };

      // Request an Earth Engine tile layer
      const mapId = await mangroves.getMap(visualization);

      // Create an Earth Engine MapLayerOverlay
      const tileSource = new ee.layers.EarthEngineTileSource(mapId);
      const overlay = new ee.layers.ImageOverlay(tileSource);

      return overlay;
    } catch (error) {
      console.error("Error in getMangroves:", error);
      return null;
    }
  };

  const handleAnalysis = async ({ startDate, endDate, threshold }) => {
    console.log("⏳ Running flood frequency analysis...");

    if (!mapRef.current) {
      console.error("Map not initialized");
      return;
    }

    if (!selectedGeom) {
      console.error("No geometry selected.");
      return;
    }

    console.log("Running flood analysis with:", {
      selectedGeom,
      startDate,
      endDate,
      threshold,
    });

    try {
      // Define Bounding Box (AOI)
      const bbox = ee.Geometry.Polygon(selectedGeom);

      // Define Date Range
      const start_date = ee.Date(startDate);
      const end_date = ee.Date(endDate);
      const low_lying_threshold = threshold; // Low-lying threshold (m)

      // Load Datasets
      const jrc = ee
        .ImageCollection("JRC/GSW1_4/MonthlyHistory")
        .filterBounds(bbox)
        .filterDate(start_date, end_date)
        .map((img) => img.clip(bbox));

      const srtm = ee.Image("USGS/SRTMGL1_003").clip(bbox);
      const worldcover = ee
        .ImageCollection("ESA/WorldCover/v200")
        .first()
        .select("Map")
        .clip(bbox);
      const buildings = ee
        .FeatureCollection("GOOGLE/Research/open-buildings/v3/polygons")
        .filterBounds(bbox);

      console.log("✅ Datasets loaded and clipped.");

      // Filter Permanent Water Mask
      const permanent_water = ee
        .Image("JRC/GSW1_4/GlobalSurfaceWater")
        .select("occurrence")
        .gte(90)
        .clip(bbox);

      const filtered_jrc = jrc.map((img) =>
        img.updateMask(permanent_water.not())
      );

      console.log("✅ Applied water mask.");

      // Flood Frequency Analysis
      const calculateFloodFrequency = (collection) => {
        const addBands = (img) => {
          const obs = img.gt(0).rename("obs");
          const water = img.select("water").eq(2).rename("water");
          return img.addBands([obs, water]).clip(bbox);
        };

        collection = collection.map(addBands);
        const total_obs = collection
          .select("obs")
          .sum()
          .rename("total_obs")
          .clip(bbox);
        const total_water = collection
          .select("water")
          .sum()
          .rename("total_water")
          .clip(bbox);
        const flood_frequency = total_water
          .divide(total_obs)
          .multiply(100)
          .rename("flood_frequency")
          .clip(bbox);

        return flood_frequency.updateMask(flood_frequency.neq(0));
      };

      const flood_frequency = calculateFloodFrequency(filtered_jrc);

      console.log("✅ Flood frequency calculated.");

      // -----------------------------
      //    FLOOD-PRONE AREAS
      // -----------------------------
      console.log("⏳ Identifying flood-prone areas...");

      const slope = ee.Terrain.slope(srtm).rename("slope");
      const flat_area = slope.lt(5).rename("flat_area");
      const low_lying = srtm.lt(low_lying_threshold).rename("low_lying");
      const flood_prone_area = flood_frequency
        .updateMask(low_lying.and(flat_area))
        .clip(bbox);

      console.log("✅ Flood-prone areas identified.");

      // -----------------------------
      //    FLOODED BUILDING ANALYSIS
      // -----------------------------
      // console.log("⏳ Analyzing flooded buildings...");

      // DO NOT CALCULATE DIRECTLY

      // Vectorize Flood-Prone Areas
      const flood_prone_vector = flood_prone_area.toInt().reduceToVectors({
        reducer: ee.Reducer.countEvery(),
        geometry: bbox,
        geometryType: "polygon",
        scale: 30,
        maxPixels: 1e8,
      });

      // const flood_prone_geom = flood_prone_vector.geometry();
      const buildings_in_aoi = buildings.filterBounds(bbox);
      // const total_buildings = await buildings_in_aoi.size().getInfo();
      // console.log(`🔍 Total buildings in AOI: ${total_buildings}`);

      // const flooded_buildings = buildings_in_aoi.filterBounds(flood_prone_geom);
      // const flooded_building_count = await flooded_buildings.size().getInfo();
      // console.log(`✅ Flooded buildings identified: ${flooded_building_count}`);

      // -----------------------------
      //    FLOODED AREA PER LULC CLASS
      // -----------------------------
      // console.log("⏳ Calculating flooded area per LULC class...");

      // const lulc_mapping = {
      //   10: "Tree cover",
      //   20: "Shrubland",
      //   30: "Grassland",
      //   40: "Cropland",
      //   50: "Built-up",
      //   60: "Bare/sparse vegetation",
      //   70: "Snow and ice",
      //   80: "Permanent water bodies",
      //   90: "Herbaceous wetland",
      //   95: "Mangroves",
      // };

      // const lulc_values = Object.keys(lulc_mapping).map(Number);
      // const landcover_masked = worldcover.updateMask(flood_prone_area);
      // const area_km2 = [];

      // for (const lulc_class of lulc_values) {
      //   const class_mask = landcover_masked.eq(lulc_class);
      //   const area_m2 = await class_mask
      //     .multiply(ee.Image.pixelArea())
      //     .reduceRegion({
      //       reducer: ee.Reducer.sum(),
      //       geometry: bbox,
      //       scale: 30,
      //       maxPixels: 1e13,
      //     })
      //     .getInfo();

      //   const flooded_area_km2 = area_m2?.Map ? area_m2["Map"] / 1e6 : 0;
      //   area_km2.push({
      //     LULC_Class: lulc_class,
      //     LULC_Name: lulc_mapping[lulc_class] || "Unknown",
      //     Flooded_Area_km2: flooded_area_km2,
      //   });
      // }

      // console.log("✅ Flooded area per LULC class calculated:", area_km2);

      // -----------------------------
      //  ADD LAYERS TO MAP
      // -----------------------------
      const visParamsFlood = {
        min: 0,
        max: 100,
        palette: ["blue", "cyan", "yellow", "yellow"],
      };

      const visParamsProne = {
        min: 0,
        max: 100,
        palette: ["white", "orange", "red"],
      };

      // Convert buildings to a black outline
      const visParamsBuildings = {
        color: "black",
      };

      const mapIdFlood = await flood_frequency.getMap(visParamsFlood);
      const floodLayer = new ee.layers.ImageOverlay(
        new ee.layers.EarthEngineTileSource(mapIdFlood)
      );

      const mapIdProne = await flood_prone_area.getMap(visParamsProne);
      const floodProneLayer = new ee.layers.ImageOverlay(
        new ee.layers.EarthEngineTileSource(mapIdProne)
      );

      const mapIdBuildings = await buildings_in_aoi
        .style(visParamsBuildings)
        .getMap();
      const buildingsLayer = new ee.layers.ImageOverlay(
        new ee.layers.EarthEngineTileSource(mapIdBuildings)
      );

      mapRef.current.overlayMapTypes.push(floodLayer);
      mapRef.current.overlayMapTypes.push(floodProneLayer);
      mapRef.current.overlayMapTypes.push(buildingsLayer);

      console.log(
        "✅ Flood frequency and flood-prone layers added to the map."
      );

      console.log("⏳ Generating download URL for flood layers...");

      // Flood Frequency Raster
      const floodDownloadUrl = await flood_frequency.getDownloadURL({
        name: "flood_frequency",
        format: "GeoTIFF",
        region: bbox,
        scale: 30,
      });

      // Flood-Prone Area Raster
      const proneDownloadUrl = await flood_prone_area.getDownloadURL({
        name: "flood_prone_area",
        format: "GeoTIFF",
        region: bbox,
        scale: 30,
      });

      console.log("✅ Download URLs created:");
      console.log("Flood Frequency:", floodDownloadUrl);
      console.log("Flood-Prone Area:", proneDownloadUrl);

      // Automatically open in new tab
      window.open(floodDownloadUrl, "_blank");
      window.open(proneDownloadUrl, "_blank");

      setIsLegendVisible(true);
    } catch (error) {
      console.error("❌ Error in flood analysis:", error);
    }
  };

  // Add analysis handler
  const handleAnalysis2 = async () => {
    console.log("Running analysis...");

    if (!mapRef.current) {
      console.error("Map not initialized");
      return;
    }

    const mangroveOverlay = await getMangroves();
    if (mangroveOverlay) {
      console.log("Adding overlay...");

      // Add the layer to the map using Earth Engine's built-in layer system
      mapRef.current.overlayMapTypes.push(mangroveOverlay);
    }
  };

  // Start drawing process
  // const startDrawing = () => {
  //   dispatch(Map.requestDrawing("polygon", t("flood.drawing.instructions")));
  // };

  // defines the steps for the tour
  const steps = [
    {
      selector: "#mapScreen",
      content: t("tour.map.start"),
    },
    {
      selector: "#imageChooserForm",
      content: t("tour.map.imageChooser.info"),
    },
    {
      selector: "#imageChooserSelect",
      content: t("tour.map.imageChooser.select"),
    },
    {
      selector: "#imageChooserLoadButton",
      content: t("tour.map.imageChooser.click"),
    },
  ];

  // create a localStorage object to check if the user has already seen the tour
  const [isTourOpen, setIsTourOpen] = useLocalStorage("showMapTour", true);

  return (
    <Box className={classes.wrapper}>
      <Grid
        container
        justifyContent="center"
        spacing={0}
        id="mapScreen"
        className={classes.mapOverlay}
      ></Grid>
      <div
        ref={mapDiv}
        style={{ width: "100%", height: "calc(100vh - 64px)" }}
      />
      <Box className={classes.controlPanel}>
        <Paper className={classes.card}>
          <div className={classes.cardHeader}>
            <Typography variant="subtitle1">
              {t("flood.analysis.title")}
            </Typography>
          </div>

          <div className={classes.cardContent}>
            {/* FloodControls Component */}
            <div className={classes.section}>
              <FloodControls
                onStartDrawing={handleStartDrawing}
                onUndo={handleUndo}
                isDrawing={isDrawing}
                hasShape={!!selectedGeom}
                onDataUpdate={handleDataUpdate}
              />
            </div>
          </div>

          {/* ✅ Centered Return to Selection Button */}
          <div className={classes.buttonContainer}>
            <Button
              color="primary"
              variant="contained"
              className={classes.button}
              onClick={returnToBegin}
            >
              {t("forms.acquisition.5.bathymetry.returnToBegin")}
            </Button>
          </div>
        </Paper>
      </Box>
      <div>{isLegendVisible && <FloodLegend />}</div>
    </Box>
  );
};

export default ProcessingPage;
